package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.api.HomeSectionApiClient
import com.castly.admin.external.Sortable
import com.castly.admin.external.createSortableOptions
import com.castly.admin.ui.components.Topbar
import com.castly.admin.ui.activity.ActivityPage
import com.castly.admin.ui.icons.*
import com.castly.admin.ui.podcasts.PodcastTypeChip
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.AppThemeProvider
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.actions.ActionsHistoryPage
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.PodcastDto
import com.castly.shared.dto.StatusDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

private fun enableThemeTransition() {
    js(
        """
        (function() {
            document.documentElement.classList.add('theme-transitioning');
            setTimeout(function() {
                document.documentElement.classList.remove('theme-transitioning');
            }, 700);
        })()
    """
    )
}

private fun persistThemePreference(isDark: Boolean) {
    window.localStorage.setItem("theme", if (isDark) "dark" else "light")
}

private fun getInitialDarkMode(): Boolean {
    return try {
        val stored = window.localStorage.getItem("theme")
        when (stored) {
            "dark" -> true
            "light" -> false
            else -> window.matchMedia("(prefers-color-scheme: dark)").matches
        }
    } catch (_: Throwable) {
        false
    }
}

private fun parseMagicToken(): String? {
    val search = window.location.search
    if (search.isBlank()) return null
    val params = search.removePrefix("?").split("&").mapNotNull {
        val parts = it.split("=")
        if (parts.size == 2) parts[0] to js("decodeURIComponent")(parts[1]) as String else null
    }.toMap()
    val token = params["magic_token"]
    if (token != null) {
        window.history.replaceState(null, "", window.location.pathname)
    }
    return token
}

sealed class UiState {
    object Loading : UiState()
    data class Success(val status: StatusDto) : UiState()
    data class Error(val message: String) : UiState()
}

// URL slug mapping for hash-based routing
private fun Section.toSlug(): String = when (this) {
    Section.Overview -> "overview"
    Section.Podcasts -> "podcasts"
    Section.Audiobooks -> "audiobooks"
    Section.Authors -> "authors"
    Section.Readers -> "readers"
    Section.Tags -> "tags"
    Section.Activity -> "activity"
    Section.Home -> "home"
    Section.ApiDocs -> "api-docs"
    Section.Analytics -> "analytics"
    Section.ActionsHistory -> "actions-history"
    Section.AudiobookGuide -> "audiobook-guide"
    Section.UserManagement -> "user-management"
    Section.Labels -> "labels"
}

private fun sectionFromSlug(slug: String): Section? = when (slug) {
    "overview" -> Section.Overview
    "podcasts" -> Section.Podcasts
    "audiobooks" -> Section.Audiobooks
    "authors" -> Section.Authors
    "readers" -> Section.Readers
    "tags" -> Section.Tags
    "activity" -> Section.Activity
    "downloads" -> Section.Activity  // legacy redirect
    "import-history" -> Section.Activity  // legacy redirect
    "home" -> Section.Home
    "api-docs" -> Section.ApiDocs
    "analytics" -> Section.Analytics
    "actions-history" -> Section.ActionsHistory
    "audiobook-guide" -> Section.AudiobookGuide
    "user-management" -> Section.UserManagement
    "labels" -> Section.Labels
    else -> null
}

private fun parseHashRoute(): Pair<Section?, PodcastType?> {
    val hash = window.location.hash.removePrefix("#").removePrefix("/")
    if (hash.isBlank()) return Pair(null, null)
    val parts = hash.split("/")
    val section = sectionFromSlug(parts[0])
    // Check for podcast type in second segment (e.g., #/podcasts/quraan)
    val podcastType = if (section == Section.Podcasts && parts.size > 1) {
        try { PodcastType.valueOf(parts[1].uppercase()) } catch (_: Exception) { null }
    } else null
    return Pair(section, podcastType)
}

enum class UserRole {
    NONE,
    VIEWER,
    LABEL_EDITOR,
    EDITOR,
    ADMIN,
    SUPER_ADMIN;

    companion object {
        fun fromString(role: String): UserRole {
            return when (role.uppercase().replace("-", "_")) {
                "SUPER_ADMIN" -> SUPER_ADMIN
                "ADMIN" -> ADMIN
                "EDITOR" -> EDITOR
                "LABEL_EDITOR" -> LABEL_EDITOR
                "VIEWER" -> VIEWER
                else -> NONE
            }
        }
    }
}

enum class Section(val label: String) {
    Overview("Overview"),
    Podcasts("Podcasts"),
    Audiobooks("Audiobooks"),
    Authors("Authors"),
    Readers("Readers"),
    Tags("Tags"),
    Activity("Activity"),
    Home("Home"),
    ApiDocs("API Documentation"),
    Analytics("Analytics"),
    ActionsHistory("Actions History"),
    AudiobookGuide("Audiobook Guide"),
    UserManagement("User Management"),
    Labels("Labels")
}

fun visibleSections(role: UserRole, isLabelAdmin: Boolean = false): List<Section> {
    val baseSections = when (role) {
        UserRole.SUPER_ADMIN -> listOf(
            Section.Overview,
            Section.Home,
            Section.Audiobooks,
            Section.Authors,
            Section.Readers,
            Section.Podcasts,
            Section.Tags,
            Section.Activity,
            Section.ApiDocs,
            Section.Analytics,
            Section.ActionsHistory,
            Section.UserManagement,
            Section.Labels,
            Section.AudiobookGuide
        )

        UserRole.ADMIN -> listOf(
            Section.Overview,
            Section.Home,
            Section.Audiobooks,
            Section.Authors,
            Section.Readers,
            Section.Podcasts,
            Section.Tags,
            Section.Activity,
            Section.ApiDocs,
            Section.Analytics,
            Section.ActionsHistory,
            Section.Labels,
            Section.AudiobookGuide
        )

        UserRole.EDITOR -> listOf(
            Section.Overview,
            Section.Audiobooks,
            Section.Authors,
            Section.Readers,
            Section.Podcasts,
            Section.Tags,
            Section.Activity,
            Section.Analytics,
            Section.ActionsHistory,
            Section.AudiobookGuide
        )

        UserRole.LABEL_EDITOR -> listOf(
            Section.Audiobooks,
            Section.Authors,
            Section.Readers,
            Section.AudiobookGuide
        )

        UserRole.VIEWER -> listOf(
            Section.Overview,
            Section.Audiobooks,
            Section.Authors,
            Section.Readers,
            Section.Podcasts,
            Section.Tags,
            Section.Activity,
            Section.Analytics,
            Section.ActionsHistory,
            Section.ApiDocs,
            Section.AudiobookGuide
        )

        UserRole.NONE -> listOf(Section.Overview, Section.AudiobookGuide)
    }

    // Add Labels section for label admins who don't already have it
    return if (isLabelAdmin && Section.Labels !in baseSections) {
        baseSections + Section.Labels
    } else {
        baseSections
    }
}

sealed class AuthState {
    object Idle : AuthState()
    object Sending : AuthState()
    data class Sent(val email: String) : AuthState()
    data class Error(val message: String) : AuthState()
    object Verifying : AuthState()
    object SessionExpired : AuthState()
}

@Composable
fun App(apiClient: ApiClient) {
    var state by remember { mutableStateOf<UiState>(UiState.Loading) }
    // Initialize section from URL hash
    val initialRoute = remember { parseHashRoute() }
    var selected by remember { mutableStateOf(initialRoute.first ?: Section.Overview) }
    var selectedPodcastType by remember { mutableStateOf<PodcastType?>(initialRoute.second) }
    var token by remember { mutableStateOf(window.localStorage.getItem("id_token")) }
    var userEmail by remember { mutableStateOf<String?>(null) }
    var userRole by remember {
        val storedRole = window.localStorage.getItem("user_role")
        mutableStateOf(if (storedRole != null) UserRole.fromString(storedRole) else UserRole.NONE)
    }
    var isLabelAdmin by remember {
        mutableStateOf(window.localStorage.getItem("is_label_admin") == "true")
    }

    // Land on a section the role can actually open. Overview is not in a label editor's
    // navigation, yet it was still the default landing page, so they arrived on a screen
    // whose stats endpoint answers 403.
    LaunchedEffect(userRole, isLabelAdmin) {
        val allowed = visibleSections(userRole, isLabelAdmin)
        if (allowed.isNotEmpty() && selected !in allowed) {
            selected = allowed.first()
        }
    }
    var authState by remember { mutableStateOf<AuthState>(AuthState.Idle) }
    var showPreview by remember { mutableStateOf(false) }
    var isDark by remember { mutableStateOf(getInitialDarkMode()) }

    fun toggleTheme() {
        val newDark = !isDark
        try {
            enableThemeTransition()
        } catch (_: Throwable) {
        }
        isDark = newDark
        try {
            persistThemePreference(newDark)
        } catch (_: Throwable) {
        }
    }

    val scope = rememberCoroutineScope()

    // Listen for OS theme changes (only applies if no stored preference)
    DisposableEffect(Unit) {
        val mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
        val listener: (Event) -> Unit = {
            try {
                if (window.localStorage.getItem("theme") == null) {
                    isDark = mediaQuery.matches
                }
            } catch (_: Throwable) {
                isDark = mediaQuery.matches
            }
        }
        mediaQuery.addEventListener("change", listener)
        onDispose {
            mediaQuery.removeEventListener("change", listener)
        }
    }

    // Sync URL hash when section changes
    LaunchedEffect(selected, selectedPodcastType) {
        val slug = selected.toSlug()
        val hash = if (selected == Section.Podcasts && selectedPodcastType != null) {
            "#/$slug/${selectedPodcastType!!.name.lowercase()}"
        } else {
            "#/$slug"
        }
        if (window.location.hash != hash) {
            window.history.pushState(null, "", hash)
        }
    }

    // Listen for browser back/forward
    DisposableEffect(Unit) {
        val popstateListener: (Event) -> Unit = {
            val (section, podcastType) = parseHashRoute()
            if (section != null) {
                selected = section
                selectedPodcastType = podcastType
            }
        }
        window.addEventListener("popstate", popstateListener)
        onDispose { window.removeEventListener("popstate", popstateListener) }
    }

    // On load: check for magic_token in URL and verify it
    LaunchedEffect(Unit) {
        // Check if session expired (set by ApiClient interceptor)
        val sessionExpired = window.sessionStorage.getItem("session_expired")
        if (sessionExpired == "true") {
            window.sessionStorage.removeItem("session_expired")
            authState = AuthState.SessionExpired
        }

        val magicToken = parseMagicToken()
        if (!magicToken.isNullOrBlank()) {
            authState = AuthState.Verifying
            try {
                val response = apiClient.verifyMagicToken(magicToken)
                window.localStorage.setItem("id_token", response.token)
                window.localStorage.setItem("user_role", response.role)
                token = response.token
                userEmail = response.email
                userRole = UserRole.fromString(response.role)
                authState = AuthState.Idle
            } catch (e: Exception) {
                authState = AuthState.Error("Failed to verify magic link: ${e.message}")
            }
        }

        // Fetch API status regardless
        state = try {
            val status = apiClient.getStatus()
            UiState.Success(status)
        } catch (e: Exception) {
            UiState.Error(e.message ?: "Failed to fetch status")
        }

        // If logged in fetch profile to validate token
        if (!token.isNullOrBlank() && userEmail == null) {
            try {
                val me = apiClient.getMe()
                userEmail = me.email ?: me.sub
                if (!me.role.isNullOrBlank()) {
                    userRole = UserRole.fromString(me.role)
                    window.localStorage.setItem("user_role", me.role)
                }
                // Fetch isLabelAdmin from permissions endpoint
                try {
                    val perms = apiClient.getMyPermissions()
                    isLabelAdmin = perms.isLabelAdmin
                    window.localStorage.setItem("is_label_admin", perms.isLabelAdmin.toString())
                    window.localStorage.setItem("user_label_id", perms.userLabelId?.toString() ?: "")
                } catch (_: Throwable) { /* non-critical */ }
            } catch (_: Throwable) {
                // Token is invalid/expired - clear it and show login
                window.localStorage.removeItem("id_token")
                window.localStorage.removeItem("user_role")
                window.localStorage.removeItem("is_label_admin")
                window.localStorage.removeItem("user_label_id")
                token = null
                userRole = UserRole.NONE
                isLabelAdmin = false
            }
        }
    }

    fun doLogout() {
        window.localStorage.removeItem("id_token")
        window.localStorage.removeItem("user_role")
        window.localStorage.removeItem("is_label_admin")
        window.localStorage.removeItem("user_label_id")
        token = null
        userEmail = null
        userRole = UserRole.NONE
        isLabelAdmin = false
        authState = AuthState.Idle
    }

    fun sendMagicLink(email: String) {
        scope.launch {
            authState = AuthState.Sending
            try {
                apiClient.sendMagicLink(email)
                authState = AuthState.Sent(email)
            } catch (e: Exception) {
                authState = AuthState.Error(e.message ?: "Failed to send magic link")
            }
        }
    }

    // Sidebar collapse state
    var sidebarCollapsed by remember { mutableStateOf(window.innerWidth < 1024) }

    // Listen for viewport width changes
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            sidebarCollapsed = window.innerWidth < 1024
        }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    val sidebarWidth = if (sidebarCollapsed) 60.px else 240.px

    // App layout: Sidebar + Content + Mobile Preview (side-by-side)
    AppThemeProvider(isDark = isDark) {
        val colors = AppTheme.colors
        Div({
            style {
                fontFamily("system-ui", "-apple-system", "sans-serif")
                display(DisplayStyle.Flex)
                height(100.vh)
                backgroundColor(Color(colors.background))
                color(Color(colors.text))
                themeTransition()
            }
        }) {
            // Sidebar when logged in, or for public pages
            if (!token.isNullOrBlank() || selected == Section.AudiobookGuide) {
                Sidebar(
                    selected = selected,
                    selectedPodcastType = selectedPodcastType,
                    onSelect = { section, type ->
                        selected = section
                        selectedPodcastType = type
                    },
                    userEmail = userEmail,
                    userRole = userRole,
                    isLoggedIn = !token.isNullOrBlank(),
                    showPreview = showPreview,
                    onTogglePreview = { showPreview = !showPreview },
                    collapsed = sidebarCollapsed,
                    onToggleCollapse = { sidebarCollapsed = !sidebarCollapsed }
                )
            }

            // Topbar (fixed) when logged in or on public pages
            val loggedIn = !token.isNullOrBlank()
            if (loggedIn || selected == Section.AudiobookGuide) {
                val currentType = selectedPodcastType
                val pageTitle = if (selected == Section.Podcasts && currentType != null) {
                    "${selected.label} / ${currentType.displayName}"
                } else {
                    selected.label
                }
                Topbar(
                    pageTitle = pageTitle,
                    isDark = isDark,
                    onToggleTheme = { toggleTheme() },
                    userEmail = userEmail,
                    onLogout = { doLogout() },
                    sidebarWidth = sidebarWidth
                )
            }

            // Content area
            Div({
                style {
                    flex(1)
                    padding(Spacing.xxl)
                    if (loggedIn || selected == Section.AudiobookGuide) paddingTop(80.px) // account for fixed topbar (64px + spacing)
                    overflow("auto")
                    if (!loggedIn && selected != Section.AudiobookGuide) {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                    }
                }
            }) {
                if (!loggedIn && selected != Section.AudiobookGuide) {
                    Div({ style { maxWidth(420.px); width(100.percent) } }) {
                        LoginCard(authState = authState, onSendMagicLink = { sendMagicLink(it) })
                    }
                } else {

                    when (selected) {
                        Section.Overview -> OverviewSection(
                            apiClient = apiClient,
                            apiState = state,
                            onNavigate = { section -> selected = section; selectedPodcastType = null },
                            onRefresh = {
                                scope.launch {
                                    state = UiState.Loading
                                    state = try {
                                        val status = apiClient.getStatus()
                                        UiState.Success(status)
                                    } catch (e: Exception) {
                                        UiState.Error(e.message ?: "Failed to fetch status")
                                    }
                                }
                            }
                        )

                        Section.Podcasts -> PodcastsSection(
                            apiClient = apiClient,
                            filterType = selectedPodcastType
                        )

                        Section.Audiobooks -> AudiobooksSection(apiClient)
                        Section.Authors -> AuthorsSection(apiClient)
                        Section.Readers -> ReadersSection(apiClient)
                        Section.Tags -> TagsSection(apiClient)
                        Section.Activity -> com.castly.admin.ui.activity.ActivityPage(apiClient)
                        Section.Home -> {
                            val baseUrl = remember {
                                if (window.location.hostname == "localhost") {
                                    "http://localhost:8080"
                                } else {
                                    "https://api-${window.location.hostname}"
                                }
                            }
                            val homeSectionApiClient = remember { HomeSectionApiClient(baseUrl) }
                            HomeSection(homeSectionApiClient)
                        }

                        Section.ApiDocs -> ApiDocsSection()
                        Section.Analytics -> com.castly.admin.ui.analytics.AnalyticsPage(apiClient)
                        // ImportHistory removed — merged into Activity
                        Section.ActionsHistory -> ActionsHistoryPage(apiClient)
                        Section.UserManagement -> com.castly.admin.ui.admin.UserManagementPage(apiClient)
                        Section.Labels -> com.castly.admin.ui.labels.LabelsPage(apiClient)
                        Section.AudiobookGuide -> AudiobookGuidePage()
                    }
                }
            }

            // Mobile preview panel (side-by-side with content)
            MobilePreview(
                apiClient = apiClient,
                isVisible = showPreview && !token.isNullOrBlank(),
                onClose = { showPreview = false }
            )
        }
    }
}

@Composable
private fun Sidebar(
    selected: Section,
    selectedPodcastType: PodcastType?,
    onSelect: (Section, PodcastType?) -> Unit,
    userEmail: String?,
    userRole: UserRole,
    isLoggedIn: Boolean,
    showPreview: Boolean = false,
    onTogglePreview: () -> Unit = {},
    collapsed: Boolean = false,
    onToggleCollapse: () -> Unit = {}
) {
    val colors = AppTheme.colors
    val isLabelAdminFlag = window.localStorage.getItem("is_label_admin") == "true"
    val allowedSections = visibleSections(userRole, isLabelAdminFlag)

    // Podcast type submenu removed - types are now filter chips on list page

    var isAudiobooksExpanded by remember { mutableStateOf(true) }
    LaunchedEffect(selected) {
        if (selected == Section.Authors || selected == Section.Readers) {
            isAudiobooksExpanded = true
        }
    }

    // podcastTypesForSidebar removed - types are now filter chips on podcast list page

    Div({
        style {
            width(if (collapsed) 60.px else 240.px)
            backgroundColor(Color(colors.sidebar))
            color(Color(colors.sidebarText))
            height(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("transition", "width 0.3s ease")
            overflow("hidden")
            property("flex-shrink", "0")
            property("border-right", "1px solid ${colors.border}")
        }
    }) {
        // Brand
        Div({
            onClick { onToggleCollapse() }
            style {
                padding(if (collapsed) Spacing.lg else Spacing.xl)
                property("border-bottom", "1px solid ${colors.border}")
                fontWeight("700")
                color(Color(colors.sidebarText))
                property("flex-shrink", "0")
                cursor("pointer")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(if (collapsed) JustifyContent.Center else JustifyContent.FlexStart)
                gap(Spacing.sm)
                property("white-space", "nowrap")
                minHeight(56.px)
            }
        }) {
            if (collapsed) {
                IconMenu(size = 20)
            } else {
                IconMenu(size = 20)
                Text("Castly Admin")
            }
        }

        // User section removed per client feedback (T022)

        // Scrollable nav items container
        Div({
            style {
                flex(1)
                overflow("auto")
                property("flex-shrink", "1")
                property("min-height", "0")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            }
        }) {
            allowedSections.forEach { section ->
                val sectionIcon: @Composable () -> Unit = when (section) {
                    Section.Overview -> {
                        { IconHome(size = 18) }
                    }

                    Section.Podcasts -> {
                        { IconHeadphones(size = 18) }
                    }

                    Section.Audiobooks -> {
                        { IconBookOpen(size = 18) }
                    }

                    Section.Authors -> {
                        { IconUsers(size = 18) }
                    }

                    Section.Readers -> {
                        { IconUsers(size = 18) }
                    }

                    Section.Tags -> {
                        { IconTag(size = 18) }
                    }

                    Section.Activity -> {
                        { IconClock(size = 18) }
                    }

                    Section.Home -> {
                        { IconLayout(size = 18) }
                    }

                    Section.ApiDocs -> {
                        { IconFileText(size = 18) }
                    }

                    Section.Analytics -> {
                        { IconBarChart(size = 18) }
                    }

                    Section.ActionsHistory -> {
                        { IconClock(size = 18) }
                    }

                    Section.UserManagement -> {
                        { IconUsers(size = 18) }
                    }

                    Section.AudiobookGuide -> {
                        { IconInfo(size = 18) }
                    }

                    Section.Labels -> {
                        { IconTag(size = 18) }
                    }
                }

                if (collapsed) {
                    // Collapsed mode: icon-only items
                    if (section != Section.Authors && section != Section.Readers) {
                        NavIconOnly(
                            icon = sectionIcon,
                            label = section.label,
                            active = selected == section,
                            onClick = { onSelect(section, null) }
                        )
                    }
                } else {
                    // Expanded mode: full nav items
                    if (section == Section.Podcasts) {
                        // Podcasts as simple nav item (types are filters on the list page)
                        NavItem(section.label, selected == section && selectedPodcastType != PodcastType.QURAAN, icon = sectionIcon) {
                            onSelect(section, null)
                        }
                        // Quran as its own top-level sidebar item
                        NavItem("Quraan", selected == section && selectedPodcastType == PodcastType.QURAAN, icon = { IconBookOpen(size = 18) }) {
                            onSelect(Section.Podcasts, PodcastType.QURAAN)
                        }
                    } else if (section == Section.Audiobooks) {
                        NavItemWithSubItems(
                            text = section.label,
                            icon = sectionIcon,
                            active = selected == section,
                            isExpanded = isAudiobooksExpanded,
                            onToggleExpand = { isAudiobooksExpanded = !isAudiobooksExpanded },
                            onClick = { onSelect(section, null) }
                        )

                        // Animated submenu container (CSS Grid for smooth animation)
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-rows", if (isAudiobooksExpanded) "1fr" else "0fr")
                                property("transition", "grid-template-rows 0.3s ease")
                            }
                        }) {
                          Div({ style { overflow("hidden") } }) {
                            if (allowedSections.contains(Section.Authors)) {
                                NavSubItem(
                                    text = Section.Authors.label,
                                    active = selected == Section.Authors,
                                    onClick = { onSelect(Section.Authors, null) }
                                )
                            }
                            if (allowedSections.contains(Section.Readers)) {
                                NavSubItem(
                                    text = Section.Readers.label,
                                    active = selected == Section.Readers,
                                    onClick = { onSelect(Section.Readers, null) }
                                )
                            }
                          }
                        }
                    } else if (section == Section.Authors || section == Section.Readers) {
                        // Skip - sub-items under Audiobooks
                    } else {
                        NavItem(section.label, selected == section, icon = sectionIcon) { onSelect(section, null) }
                    }
                }
            }
        }

        // Preview toggle button
        if (isLoggedIn) {
            Div({
                onClick { onTogglePreview() }
                style {
                    padding(14.px, if (collapsed) 0.px else Spacing.xl)
                    cursor("pointer")
                    backgroundColor(if (showPreview) Color(colors.primary) else Color.transparent)
                    color(if (showPreview) Color("#ffffff") else Color(colors.sidebarText))
                    property("border-top", "1px solid ${colors.border}")
                    property("flex-shrink", "0")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(if (collapsed) JustifyContent.Center else JustifyContent.FlexStart)
                    gap(Spacing.sm)
                    property("white-space", "nowrap")
                }
            }) {
                Span({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                    IconEye(size = 18)
                }
                if (!collapsed) Text("Client Preview")
            }
        }

    }
}

@Composable
private fun NavItem(text: String, active: Boolean, icon: (@Composable () -> Unit)? = null, onClick: () -> Unit) {
    val colors = AppTheme.colors
    val bg = if (active) Color(colors.sidebarActive) else Color("transparent")
    val textColor = if (active) Color(colors.primary) else Color(colors.sidebarText)

    Button({
        onClick { onClick() }
        attr("role", "button")
        attr("aria-label", text)
        style {
            width(100.percent)
            padding(14.px, Spacing.xl)
            backgroundColor(bg)
            color(textColor)
            cursor("pointer")
            property("user-select", "none")
            border(0.px)
            textAlign("left")
            fontSize(Typography.base)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
        }
    }) {
        if (icon != null) {
            Span({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) { icon() }
        }
        Text(text)
    }
}

@Composable
private fun NavItemWithSubItems(
    text: String,
    icon: (@Composable () -> Unit)? = null,
    active: Boolean,
    isExpanded: Boolean,
    onToggleExpand: () -> Unit,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val bg = if (active) Color(colors.sidebarActive) else Color("transparent")
    val textColor = if (active) Color(colors.primary) else Color(colors.sidebarText)

    Button({
        onClick {
            onClick()
            if (!isExpanded) onToggleExpand()
        }
        attr("role", "button")
        attr("aria-label", text)
        style {
            width(100.percent)
            padding(14.px, Spacing.xl)
            backgroundColor(bg)
            color(textColor)
            cursor("pointer")
            property("user-select", "none")
            border(0.px)
            textAlign("left")
            fontSize(Typography.base)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
        }
    }) {
        if (icon != null) {
            Span({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) { icon() }
        }
        Span({ style { flex(1) } }) { Text(text) }
        Span({
            onClick { event ->
                event.stopPropagation()
                onToggleExpand()
            }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("transition", "transform 0.2s ease")
                property("transform", if (isExpanded) "rotate(90deg)" else "rotate(0deg)")
            }
        }) { IconChevronRight(size = 16) }
    }
}

@Composable
private fun NavSubItem(text: String, active: Boolean, onClick: () -> Unit) {
    val colors = AppTheme.colors
    val bg = if (active) Color(colors.sidebarActive) else Color("transparent")
    val textColor = if (active) Color(colors.primary) else Color(colors.sidebarText)

    Button({
        onClick { onClick() }
        attr("role", "button")
        attr("aria-label", text)
        style {
            width(100.percent)
            padding(Spacing.md, Spacing.xl)
            paddingLeft(40.px)
            backgroundColor(bg)
            color(textColor)
            cursor("pointer")
            property("user-select", "none")
            border(0.px)
            textAlign("left")
            fontSize(Typography.sm)
        }
    }) { Text(text) }
}

@Composable
private fun NavIconOnly(
    icon: @Composable () -> Unit,
    label: String,
    active: Boolean,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val bg = if (active) Color(colors.sidebarActive) else Color("transparent")
    val textColor = if (active) Color(colors.primary) else Color(colors.sidebarText)

    com.castly.admin.ui.components.Tooltip(text = label) {
        Button({
            onClick { onClick() }
            attr("aria-label", label)
            style {
                width(60.px)
                property("min-width", "60px")
                padding(14.px, 0.px)
                backgroundColor(bg)
                color(textColor)
                cursor("pointer")
                border(0.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("flex-shrink", "0")
            }
        }) { icon() }
    }
}

@Composable
private fun OverviewSection(apiClient: ApiClient, apiState: UiState, onNavigate: (Section) -> Unit = {}, onRefresh: () -> Unit) {
    val colors = AppTheme.colors
    var dashboardStats by remember { mutableStateOf<com.castly.shared.dto.DashboardStatsDto?>(null) }
    var statsLoading by remember { mutableStateOf(true) }
    var statsError by remember { mutableStateOf<String?>(null) }
    // Roles without the analytics vertical (label editors) get a 403 here. That is the
    // expected answer for them, not a failure, so the stats are hidden entirely rather
    // than replaced with an error they can do nothing about.
    var statsForbidden by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        try {
            dashboardStats = apiClient.getDashboardStats()
        } catch (e: com.castly.admin.api.ApiException) {
            if (e.isForbidden) statsForbidden = true else statsError = e.message
        } catch (e: Exception) {
            statsError = e.message
        } finally {
            statsLoading = false
        }
    }

    val stats = dashboardStats

    if (!statsForbidden) {
        DashboardStatCards(stats, statsLoading, statsError, colors, onNavigate)
    }


    // API Status card (de-emphasized)
    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(800.px)
            themeTransition()
        }
    }) {
        H2({
            style {
                marginTop(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.lg)
                color(Color(colors.textMuted))
            }
        }) { Text("API Status") }

        when (apiState) {
            is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
            is UiState.Success -> StatusCard(apiState.status)
            is UiState.Error -> Div({
                style { errorMessageStyle(colors) }
            }) { Text("Error: ${apiState.message}") }
        }

        Button({
            onClick { onRefresh() }
            style {
                marginTop(Spacing.lg)
                secondaryButtonStyle(colors = colors)
            }
        }) { Text("Refresh Status") }
    }
}

@Composable
fun StatusCard(status: StatusDto) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "auto 1fr")
            property("gap", "${Spacing.sm} ${Spacing.lg}")
        }
    }) {
        StatusRow("Status", status.status, colors.success)
        StatusRow("Version", status.version, colors.textMuted)
        StatusRow("Timestamp", status.timestamp, colors.textMuted)
    }
}

@Composable
fun StatusRow(label: String, value: String, valueColor: String) {
    val colors = AppTheme.colors
    Span({
        style {
            fontWeight("600")
            color(Color(colors.textMuted))
        }
    }) { Text("$label:") }
    Span({ style { color(Color(valueColor)) } }) { Text(value) }
}

@Composable
private fun PlaceholderCard(title: String, body: String) {
    val colors = AppTheme.colors
    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(800.px)
            themeTransition()
        }
    }) {
        H2({ style { margin(0.px); marginBottom(Spacing.sm); fontSize(Typography.xl); color(Color(colors.text)) } }) {
            Text(
                title
            )
        }
        P({ style { color(Color(colors.textMuted)) } }) { Text(body) }
    }
}

@Composable
private fun LoginCard(authState: AuthState, onSendMagicLink: (String) -> Unit) {
    val colors = AppTheme.colors
    var email by remember { mutableStateOf("") }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(400.px)
            themeTransition()
        }
    }) {
        H2({ style { margin(0.px); marginBottom(Spacing.sm); fontSize(Typography.xl); color(Color(colors.text)) } }) {
            Text(
                "Sign In"
            )
        }

        when (authState) {
            is AuthState.SessionExpired -> {
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#fef3c7" else "#451a03"))
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.md)
                    }
                }) {
                    P({ style { margin(0.px); color(Color(colors.warning)); fontWeight("500") } }) {
                        Text("Session Expired")
                    }
                    P({ style { margin(0.px); marginTop(Spacing.sm); color(Color(colors.textMuted)); fontSize(Typography.base) } }) {
                        Text("Your session has expired. Please sign in again.")
                    }
                }
                EmailForm(
                    email,
                    authState is AuthState.Sending,
                    onEmailChange = { email = it },
                    onSubmit = { onSendMagicLink(email) })
            }

            is AuthState.Verifying -> {
                P({ style { color(Color(colors.textMuted)) } }) { Text("Verifying your magic link...") }
            }

            is AuthState.Sent -> {
                Div({
                    style {
                        padding(Spacing.lg)
                        successMessageStyle(colors)
                    }
                }) {
                    P({ style { margin(0.px); fontWeight("500") } }) {
                        Text("Check your email!")
                    }
                    P({ style { margin(0.px); marginTop(Spacing.sm); fontSize(Typography.base) } }) {
                        Text("We sent a magic link to ${authState.email}")
                    }
                }
                val emailDomain = authState.email.substringAfter("@").lowercase()
                val showTwist = emailDomain == "twistmena.com"
                val showEand = emailDomain == "etisalat.com" || emailDomain == "eand.com.eg"
                if (showTwist || showEand) {
                    Div({
                        style {
                            marginTop(Spacing.lg)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(Spacing.sm)
                        }
                    }) {
                        P({ style { margin(0.px); marginBottom(Spacing.xs); color(Color(colors.textMuted)); fontSize(Typography.sm) } }) {
                            Text("Open your mailbox:")
                        }
                        if (showTwist) {
                            A(
                                href = "https://webmail.mail.eu-west-1.awsapps.com/workmail/?organization=twistmena",
                                attrs = {
                                    style {
                                        display(DisplayStyle.Block)
                                        padding(12.px, Spacing.lg)
                                        backgroundColor(Color(colors.primary))
                                        color(Color("#ffffff"))
                                        borderRadius(BorderRadius.md)
                                        textAlign("center")
                                        fontWeight("500")
                                        fontSize(Typography.base)
                                        property("text-decoration", "none")
                                    }
                                }
                            ) {
                                Text("Open Twist Mail")
                            }
                        }
                        if (showEand) {
                            A(
                                href = "https://mail.etisalat.com",
                                attrs = {
                                    style {
                                        display(DisplayStyle.Block)
                                        padding(12.px, Spacing.lg)
                                        backgroundColor(Color(colors.surface))
                                        color(Color(colors.text))
                                        borderRadius(BorderRadius.md)
                                        textAlign("center")
                                        fontWeight("500")
                                        fontSize(Typography.base)
                                        property("text-decoration", "none")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                    }
                                }
                            ) {
                                Text("Open e& Mail")
                            }
                        }
                    }
                }
            }

            is AuthState.Error -> {
                Div({
                    style { errorMessageStyle(colors) }
                }) {
                    P({ style { margin(0.px); fontSize(Typography.base) } }) {
                        Text(authState.message)
                    }
                }
                EmailForm(
                    email,
                    authState is AuthState.Sending,
                    onEmailChange = { email = it },
                    onSubmit = { onSendMagicLink(email) })
            }

            else -> {
                P({ style { color(Color(colors.textMuted)); marginBottom(Spacing.lg) } }) {
                    Text("Enter your email to receive a magic sign-in link.")
                }
                EmailForm(
                    email,
                    authState is AuthState.Sending,
                    onEmailChange = { email = it },
                    onSubmit = { onSendMagicLink(email) })
            }
        }
    }
}

@Composable
private fun EmailForm(email: String, isLoading: Boolean, onEmailChange: (String) -> Unit, onSubmit: () -> Unit) {
    val colors = AppTheme.colors
    Form(action = null, attrs = {
        onSubmit {
            it.preventDefault()
            if (email.isNotBlank()) onSubmit()
        }
    }) {
        Input(InputType.Email) {
            value(email)
            onInput { onEmailChange(it.value) }
            attr("placeholder", "you@example.com")
            attr("required", "true")
            attr("autocomplete", "email")
            attr("name", "email")
            style {
                formInputStyle(colors)
                property("box-sizing", "border-box")
            }
        }
        Button({
            attr("type", "submit")
            if (isLoading || email.isBlank()) attr("disabled", "true")
            style {
                marginTop(Spacing.md)
                width(100.percent)
                primaryButtonStyle(disabled = isLoading || email.isBlank(), colors = colors)
            }
        }) {
            Text(if (isLoading) "Sending..." else "Send Magic Link")
        }
    }
}

/**
 * The dashboard's stat card grids.
 *
 * Extracted so the whole block can be omitted for roles without analytics access,
 * rather than rendering a permission error the user can't act on.
 */
@Composable
private fun DashboardStatCards(
    stats: com.castly.shared.dto.DashboardStatsDto?,
    statsLoading: Boolean,
    statsError: String?,
    colors: com.castly.admin.ui.theme.AppColors,
    onNavigate: (Section) -> Unit
) {
    // Content stats
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(240px, 1fr))")
            gap(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        com.castly.admin.ui.components.StatCard(
            title = "Podcasts",
            value = if (statsLoading) "..." else "${stats?.podcasts?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconHeadphones(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Podcasts) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Audiobooks",
            value = if (statsLoading) "..." else "${stats?.audiobooks?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconBookOpen(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Audiobooks) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Episodes",
            value = if (statsLoading) "..." else "${stats?.episodes?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconFileText(size = 24) },
            details = if (!statsLoading && stats != null) mapOf(
                "Podcast episodes" to stats.episodes.podcastEpisodes,
                "Audiobook episodes" to stats.episodes.audiobookEpisodes
            ) else null,
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Podcasts) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Content Types",
            value = if (statsLoading) "..." else "${stats?.contentTypes?.size ?: 0}",
            icon = { com.castly.admin.ui.icons.IconBarChart(size = 24) },
            details = stats?.contentTypes,
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Podcasts) }
        )
    }

    // People & metadata stats
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(240px, 1fr))")
            gap(Spacing.lg)
            marginBottom(Spacing.xxl)
        }
    }) {
        com.castly.admin.ui.components.StatCard(
            title = "Podcasters",
            value = if (statsLoading) "..." else "${stats?.podcasters?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconUsers(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Podcasts) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Authors",
            value = if (statsLoading) "..." else "${stats?.authors?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconUsers(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Authors) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Readers",
            value = if (statsLoading) "..." else "${stats?.readers?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconUsers(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Readers) }
        )

        com.castly.admin.ui.components.StatCard(
            title = "Tags",
            value = if (statsLoading) "..." else "${stats?.tags?.total ?: 0}",
            icon = { com.castly.admin.ui.icons.IconTag(size = 24) },
            isLoading = statsLoading,
            onClick = { onNavigate(Section.Tags) }
        )

        val homeSectionCount = stats?.homeSections?.active ?: 0
        if (statsLoading || homeSectionCount > 0) {
            com.castly.admin.ui.components.StatCard(
                title = "Home Sections",
                value = if (statsLoading) "..." else "$homeSectionCount",
                icon = { com.castly.admin.ui.icons.IconLayout(size = 24) },
                subtitle = "Active sections",
                isLoading = statsLoading,
                onClick = { onNavigate(Section.Home) }
            )
        }
    }

    if (statsError != null) {
        Div({
            style {
                errorMessageStyle(colors)
                marginBottom(Spacing.lg)
            }
        }) { Text("Failed to load stats: $statsError") }
    }
}
